import { motion } from "framer-motion";
import { TID } from "@/lib/testIds";
import Spotlight from "@/components/Spotlight";

const IMG_HOME =
  "https://images.pexels.com/photos/5082055/pexels-photo-5082055.jpeg?auto=compress&cs=tinysrgb&w=1800";
const IMG_BUILDER =
  "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1800&q=85";

export default function Audiences({ onBook }) {
  return (
    <section
      id="audiences"
      data-testid={TID.audiences.root}
      className="relative bg-cream"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 pt-24 md:pt-32">
        <p className="text-[11px] md:text-[12px] tracking-[0.32em] uppercase text-ink">
          Who we work with
        </p>
        <h2 className="mt-8 font-display font-light text-[10vw] md:text-[5.6vw] leading-[0.98] tracking-tightest text-charcoal max-w-5xl">
          Premium joinery & kitchen specialists trusted by homeowners,
          builders, and developers.
        </h2>
      </div>

      <div className="mt-20 md:mt-28 grid md:grid-cols-2">
        {[
          {
            tid: TID.audiences.homeownerCard,
            tag: "For homeowners",
            title: "Create a home you'll love living in.",
            body: "Custom kitchens, wardrobes, laundries, entertainment units and bespoke joinery designed around your lifestyle.",
            cta: "Explore home projects",
            ctaTid: TID.audiences.homeownerCta,
            img: IMG_HOME,
            bg: "bg-sand text-charcoal",
            anchor: "portfolio",
            audience: "homeowner",
          },
          {
            tid: TID.audiences.builderCard,
            tag: "For builders & developers",
            title: "A joinery partner you can depend on.",
            body: "Reliable manufacturing, scalable production and professional installation for custom homes, duplexes, townhouses and multi-unit developments.",
            cta: "Explore trade projects",
            ctaTid: TID.audiences.builderCta,
            img: IMG_BUILDER,
            bg: "bg-charcoal text-cream",
            anchor: "trade",
            audience: "builder",
          },
        ].map((a) => (
          <Spotlight
            key={a.tid}
            tone={a.audience === "builder" ? "dark" : "light"}
            radius={420}
            className={`${a.bg} group overflow-hidden`}
          >
            <motion.article
              data-testid={a.tid}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
            <div className="aspect-[5/4] md:aspect-[5/6] overflow-hidden">
              <img
                src={a.img}
                alt={a.title}
                className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.05]"
              />
            </div>
            <div className="px-8 md:px-12 py-12 md:py-16">
              <p
                className={`text-[11px] tracking-[0.32em] uppercase ${
                  a.audience === "builder" ? "text-brass" : "text-brassDark"
                }`}
              >
                {a.tag}
              </p>
              <h3 className="mt-6 font-display font-light text-3xl md:text-5xl leading-tight tracking-tight max-w-md">
                {a.title}
              </h3>
              <p
                className={`mt-6 max-w-md leading-relaxed ${
                  a.audience === "builder" ? "text-cream/75" : "text-ink"
                }`}
              >
                {a.body}
              </p>
              <button
                data-testid={a.ctaTid}
                onClick={() => {
                  const el = document.getElementById(a.anchor);
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className={`mt-10 inline-flex items-center gap-3 text-[12px] tracking-[0.28em] uppercase pb-2 border-b transition-colors ${
                  a.audience === "builder"
                    ? "text-cream border-cream/40 hover:border-brass hover:text-brass"
                    : "text-charcoal border-charcoal/40 hover:border-brassDark hover:text-brassDark"
                }`}
              >
                {a.cta}
                <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">
                  →
                </span>
              </button>
            </div>
            </motion.article>
          </Spotlight>
        ))}
      </div>
    </section>
  );
}
